import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { bookingAPI, paymentAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import LinkDisabledCardElement from '../../components/payments/LinkDisabledCardElement';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  CreditCardIcon,
  ExclamationTriangleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

function PaymentPage() {
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'succeeded' | 'failed'>('idle');

  const { data: reservation, isLoading: reservationLoading } = useQuery(
    ['reservation', reservationId],
    () => bookingAPI.getReservation(reservationId!),
    {
      enabled: !!reservationId,
      select: (response) => response.data,
      onError: () => {
        toast.error('Reservation not found');
        navigate('/reservations');
      }
    }
  );

  const { data: paymentIntent, refetch: refetchPaymentIntent } = useQuery(
    ['payment-intent', reservation?.payment?.paymentIntentId],
    () => paymentAPI.getPaymentIntent(reservation!.payment.paymentIntentId),
    {
      enabled: !!reservation?.payment?.paymentIntentId,
      select: (response) => response.data,
      refetchInterval: (data) => {
        // Stop polling if payment is complete
        return data?.status === 'succeeded' ? false : 2000;
      }
    }
  );

  useEffect(() => {
    if (paymentIntent?.status === 'succeeded') {
      setPaymentStatus('succeeded');
      toast.success('Payment successful!');
      setTimeout(() => navigate('/reservations'), 2000);
    }
  }, [paymentIntent?.status, navigate]);

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements || !reservation?.payment?.clientSecret) {
      toast.error('Payment system not ready');
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('processing');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      toast.error('Card element not found');
      setIsProcessing(false);
      setPaymentStatus('idle');
      return;
    }

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        reservation.payment.clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              email: 'customer@example.com', // You would get this from user context
            },
          }
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === 'succeeded') {
        setPaymentStatus('succeeded');
        toast.success('Payment successful!');
        refetchPaymentIntent();
      }
    } catch (error: any) {
      console.error('Payment failed:', error);
      toast.error(error.message || 'Payment failed');
      setPaymentStatus('failed');
    } finally {
      setIsProcessing(false);
    }
  };

  if (reservationLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <LoadingSpinner className="py-20" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-on-surface">Reservation Not Found</h1>
          <p className="mt-2 text-gray-600">The reservation you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'succeeded') {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-on-surface mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">Your reservation has been confirmed.</p>
          <div className="card p-6 max-w-md mx-auto">
            <h3 className="font-medium text-on-surface mb-2">Reservation Details</h3>
            <p className="text-sm text-gray-600">
              {reservation.vehicle?.make} {reservation.vehicle?.model}
            </p>
            <p className="text-sm text-gray-600">
              {new Date(reservation.startDate).toLocaleDateString()} - {new Date(reservation.endDate).toLocaleDateString()}
            </p>
            <p className="text-sm font-medium text-on-surface mt-2">
              Total: ${reservation.totalPrice}
            </p>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            Redirecting to your reservations...
          </p>
        </div>
      </div>
    );
  }

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-on-surface">Complete Your Payment</h1>
        <p className="mt-2 text-gray-600">Secure your reservation with payment</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Reservation Summary */}
        <div className="card p-6">
          <h3 className="text-lg font-medium text-on-surface mb-4">Reservation Summary</h3>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">🚗</div>
              <div>
                <h4 className="font-medium">{reservation.vehicle?.make} {reservation.vehicle?.model}</h4>
                <p className="text-sm text-gray-600">{reservation.vehicle?.location}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-on-surface">Pickup</p>
                <p className="text-sm text-gray-600">
                  {new Date(reservation.startDate).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(reservation.startDate).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-on-surface">Return</p>
                <p className="text-sm text-gray-600">
                  {new Date(reservation.endDate).toLocaleDateString()}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(reservation.endDate).toLocaleTimeString()}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between text-lg font-semibold">
                <span>Total Amount</span>
                <span>${reservation.totalPrice}</span>
              </div>
              {reservation.depositAmount && (
                <p className="text-sm text-gray-600 mt-1">
                  Including ${reservation.depositAmount} security deposit
                </p>
              )}
            </div>
          </div>

          {/* Payment Status Indicator */}
          {reservation.status === 'pending' && (
            <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm font-medium text-yellow-800">Payment Required</p>
                <p className="text-xs text-yellow-600">
                  Complete payment to confirm your reservation
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Payment Form */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <CreditCardIcon className="h-6 w-6 text-gray-600" />
            <h3 className="text-lg font-medium text-on-surface">Payment Information</h3>
          </div>

          <form onSubmit={handlePayment}>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Card Details
              </label>
              <div className="p-3 border border-ghost rounded-lg">
                <LinkDisabledCardElement options={cardElementOptions} />
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  I agree to the{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-700">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-primary-600 hover:text-primary-700">
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={!stripe || isProcessing || (paymentStatus as any) === 'succeeded'}
              className="w-full btn-primary flex items-center justify-center space-x-2"
            >
              {isProcessing ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCardIcon className="h-5 w-5" />
                  <span>Pay ${reservation.totalPrice}</span>
                </>
              )}
            </button>

            {paymentStatus === 'failed' && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                <p className="text-sm text-red-800">
                  Payment failed. Please check your card details and try again.
                </p>
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                🔒 Your payment information is secure and encrypted
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Expiration Warning */}
      {reservation.expiresAt && (
        <div className="mt-8 card p-4 bg-orange-50 border-orange-200">
          <div className="flex items-center space-x-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
            <div>
              <p className="text-sm font-medium text-orange-800">Reservation Expires Soon</p>
              <p className="text-xs text-orange-600">
                Complete payment by {new Date(reservation.expiresAt).toLocaleString()} to secure your booking
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaymentPage;
